import React, { useState, useRef, useEffect } from 'react';
import { X, Minus, Send, CheckCheck, Check, RefreshCw } from 'lucide-react';
import { useChatPopup } from '../contexts/ChatPopupContext';
import { useAuth } from '../App';

const ChatWindow = ({ window, onClose, onMinimize, onBringToFront }) => {
  const { user } = useAuth();
  const {
    sendMessageInWindow,
    sendTypingIndicator,
    typingStatus,
    updateDraft,
    wsEnabled,
    refreshMessages
  } = useChatPopup();

  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current && !window.minimized) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [window.messages, window.minimized]);

  useEffect(() => {
    if (!window.minimized && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [window.minimized]);

  const handleSendMessage = async () => {
    if (!window.draft.trim() || sending) return;

    setSending(true);
    try {
      await sendMessageInWindow(window.id, window.draft);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    } catch (error) {
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleTextareaChange = (e) => {
    const value = e.target.value;
    updateDraft(window.id, value);
    
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';

    sendTypingIndicator(window.conversation.id, true);
    
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      sendTypingIndicator(window.conversation.id, false);
    }, 1000);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    return date.toLocaleDateString();
  };

  const getStatusIcon = (msg) => {
    if (msg.sender_id !== user?.id) return null;
    
    if (msg.read) {
      return <CheckCheck className="w-3 h-3 text-blue-500" title="Read" />;
    } else if (msg.status === 'delivered') {
      return <CheckCheck className="w-3 h-3 text-gray-500" title="Delivered" />;
    } else {
      return <Check className="w-3 h-3 text-gray-500" title="Sent" />;
    }
  };

  const isTyping = typingStatus[window.conversation.id] && 
    Object.keys(typingStatus[window.conversation.id]).some(uid => uid !== user?.id);

  if (window.minimized) {
    return (
      <div
        onClick={() => onBringToFront(window.id)}
        className="bg-gray-900 border border-gray-700 rounded-lg shadow-2xl cursor-pointer hover:bg-gray-800 transition-colors"
        style={{
          width: '280px',
          position: 'fixed',
          bottom: '16px',
          right: window.position.right + 'px',
          zIndex: window.zIndex
        }}
      >
        <div className="p-3 flex items-center space-x-3">
          {window.conversation.other_user.avatar_url ? (
            <img
              src={window.conversation.other_user.avatar_url}
              alt={window.conversation.other_user.display_name}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center">
              <span className="text-white font-bold">
                {window.conversation.other_user.display_name.charAt(0)}
              </span>
            </div>
          )}
          
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold truncate">
              {window.conversation.other_user.display_name}
            </p>
            <p className="text-xs text-gray-400">Click to open</p>
          </div>

          {window.unreadCount > 0 && (
            <span className="bg-orange-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
              {window.unreadCount > 9 ? '9+' : window.unreadCount}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={() => onBringToFront(window.id)}
      className="bg-gray-900 border border-gray-700 rounded-lg shadow-2xl flex flex-col"
      style={{
        width: '360px',
        height: '500px',
        position: 'fixed',
        bottom: window.position.bottom + 'px',
        right: window.position.right + 'px',
        zIndex: window.zIndex
      }}
    >
      <div className="bg-gray-800 border-b border-gray-700 p-3 flex items-center justify-between rounded-t-lg">
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          {window.conversation.other_user.avatar_url ? (
            <img
              src={window.conversation.other_user.avatar_url}
              alt={window.conversation.other_user.display_name}
              className="w-8 h-8 rounded-full object-cover"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center">
              <span className="text-white font-bold text-sm">
                {window.conversation.other_user.display_name.charAt(0)}
              </span>
            </div>
          )}
          
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold text-sm truncate">
              {window.conversation.other_user.display_name}
            </p>
            {isTyping ? (
              <p className="text-xs text-gray-400">typing...</p>
            ) : !wsEnabled ? (
              <p className="text-xs text-yellow-500/70">Refresh for new messages</p>
            ) : null}
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {!wsEnabled && (
            <button
              onClick={async (e) => {
                e.stopPropagation();
                setRefreshing(true);
                await refreshMessages(window.id);
                setRefreshing(false);
              }}
              disabled={refreshing}
              className="p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
              title="Refresh messages"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onMinimize(window.id);
            }}
            className="p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
          >
            <Minus className="w-5 h-5" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose(window.id);
            }}
            className="p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-black">
        {window.loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500"></div>
          </div>
        ) : window.messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-500 text-sm">No messages yet</p>
          </div>
        ) : (
          window.messages.map((msg, index) => {
            const isOwnMessage = msg.sender_id === user?.id;
            const showAvatar = index === 0 || window.messages[index - 1].sender_id !== msg.sender_id;

            return (
              <div key={msg.id} className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                <div className={`flex items-end space-x-2 max-w-[75%] ${isOwnMessage ? 'flex-row-reverse space-x-reverse' : ''}`}>
                  {!isOwnMessage && showAvatar && (
                    window.conversation.other_user.avatar_url ? (
                      <img
                        src={window.conversation.other_user.avatar_url}
                        alt={window.conversation.other_user.display_name}
                        className="w-6 h-6 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center">
                        <span className="text-white text-xs font-bold">
                          {window.conversation.other_user.display_name.charAt(0)}
                        </span>
                      </div>
                    )
                  )}
                  {!isOwnMessage && !showAvatar && <div className="w-6"></div>}

                  <div>
                    <div className={`rounded-2xl px-3 py-2 ${isOwnMessage ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white' : 'bg-gray-800 text-white'}`}>
                      <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                    </div>
                    <div className={`flex items-center mt-1 space-x-1 text-xs text-gray-500 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                      <span title={new Date(msg.created_at).toLocaleString()}>{formatTime(msg.created_at)}</span>
                      {getStatusIcon(msg)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="border-t border-gray-700 p-3 bg-gray-900 rounded-b-lg">
        <div className="flex items-end space-x-2">
          <textarea
            ref={textareaRef}
            value={window.draft}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            rows="1"
            className="flex-1 bg-gray-800 text-white placeholder-gray-500 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none max-h-[120px]"
            style={{ minHeight: '40px' }}
          />
          <button
            onClick={handleSendMessage}
            disabled={!window.draft.trim() || sending}
            className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 disabled:from-gray-600 disabled:to-gray-600 disabled:cursor-not-allowed text-white rounded-full p-2 transition-all"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-1">Enter to send, Shift+Enter for new line</p>
      </div>
    </div>
  );
};

const ChatPopup = () => {
  const {
    windows,
    closeWindow,
    minimizeWindow,
    maximizeWindow,
    bringToFront
  } = useChatPopup();

  return (
    <>
      {windows.map(window => (
        <ChatWindow
          key={window.id}
          window={window}
          onClose={closeWindow}
          onMinimize={minimizeWindow}
          onBringToFront={window.minimized ? maximizeWindow : bringToFront}
        />
      ))}
    </>
  );
};

export default ChatPopup;
